import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Button, Card, Tag, Space, message, Row, Col, Spin, Avatar, Breadcrumb, Table, Typography, Empty, Modal } from "antd";
import { ArrowLeftOutlined, EyeOutlined, FilePdfOutlined, UserOutlined, ClockCircleOutlined, CheckCircleOutlined, WarningOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import moment from "moment";
import { postReq } from "../api";
import AttendanceRegularizationDialog from "../components/AttendanceRegularizationModal.jsx";
import PayrollRecords from "./PayrollRecords";

const { Title, Text } = Typography;

const UserDetailView = () => {
  const { id } = useParams();
  const history = useHistory();

  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Main Tabs State
  const [activeMainTab, setActiveMainTab] = useState("documents");

  // Document active state
  const [activeDocKey, setActiveDocKey] = useState("resume");

  // Attendance history states
  const [month, setMonth] = useState(dayjs().format("YYYY-MM"));
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [openCorrection, setOpenCorrection] = useState(false);

  const fetchUserDetails = async () => {
    setLoading(true);
    try {
      const res = await postReq("/user/get-user", { _id: id });
      if (res && res.status === 1) {
        setCurrentUser(res.data);
      } else {
        message.error(res.message || "Failed to load user details");
        history.push("/company/users");
      }
    } catch (err) {
      console.error(err);
      message.error("Error loading user details");
      history.push("/company/users");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserAttendanceHistory = async () => {
    if (activeMainTab !== "attendance") return;
    setAttendanceLoading(true);
    try {
      const historyRes = await postReq("/user/getHistory", {
        userAttendanceId: id,
        month: month, // YYYY-MM
      });

      if (historyRes && historyRes.status === 200) {
        setAttendanceHistory(historyRes.data || []);
      } else {
        message.error("Failed to load user attendance history");
        setAttendanceHistory([]);
      }
    } catch (err) {
      console.error(err);
      message.error("Error loading user attendance");
      setAttendanceHistory([]);
    } finally {
      setAttendanceLoading(false);
    }
  };

  useEffect(() => {
    fetchUserDetails();
  }, [id]);

  useEffect(() => {
    fetchUserAttendanceHistory();
  }, [activeMainTab, month]);

  const getFileUrl = (filename) => {
    if (!filename) return "#";
    const apiBase = process.env.REACT_APP_API_URL || "http://localhost:9003/api";
    if (apiBase.includes("localhost")) {
      return `${apiBase.replace("/api", "")}/files/${filename}`;
    }
    return `${apiBase}/files/${filename}`;
  };

  const isImageFile = (filename) => {
    if (!filename) return false;
    const ext = filename.split(".").pop().toLowerCase();
    return ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
  };

  const downloadAllDocs = () => {
    if (!currentUser) return;
    const docs = [
      currentUser.resume,
      currentUser.aadharCard,
      currentUser.degreeCertificate,
      currentUser.document1,
      currentUser.document2,
    ].filter(Boolean);

    docs.forEach((filename, index) => {
      setTimeout(() => {
        const link = document.createElement("a");
        link.href = getFileUrl(filename);
        link.setAttribute("download", filename);
        link.setAttribute("target", "_blank");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }, index * 300);
    });
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <Spin size="large" tip="Loading user details..." />
      </div>
    );
  }

  if (!currentUser) return null;

  const docTabs = [
    currentUser.resume ? { key: "resume", label: "Resume", filename: currentUser.resume } : null,
    currentUser.aadharCard ? { key: "aadhar", label: "Aadhar Card", filename: currentUser.aadharCard } : null,
    currentUser.degreeCertificate ? { key: "degree", label: "Degree Certificate", filename: currentUser.degreeCertificate } : null,
    currentUser.document1 ? { key: "doc1", label: "Document 1", filename: currentUser.document1 } : null,
    currentUser.document2 ? { key: "doc2", label: "Document 2", filename: currentUser.document2 } : null,
  ].filter(Boolean);

  const selectedDoc = docTabs.find((tab) => tab.key === activeDocKey) || docTabs[0];

  const todayKey = dayjs().format("DD/MM/YYYY");

  const attendanceColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (val) => (
        <Text style={{ color: "#13487D", fontWeight: 600 }}>{val}</Text>
      ),
    },
    {
      title: "Punch In",
      dataIndex: "loginTime",
      key: "loginTime",
      render: (val) =>
        val ? (
          <span style={{ color: "#3699FF", fontWeight: 500 }}>
            {moment(val).format("hh:mm A")}
          </span>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Punch Out",
      dataIndex: "logoutTime",
      key: "logoutTime",
      render: (val) =>
        val ? (
          <span style={{ color: "#13487D", fontWeight: 500 }}>
            {moment(val).format("hh:mm A")}
          </span>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Duration",
      dataIndex: "workingHour",
      key: "workingHour",
      render: (val) =>
        val ? (
          <Tag icon={<ClockCircleOutlined />} color="blue">
            {val}h
          </Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (val) => {
        const isPresent = val === "Present";
        return (
          <Tag
            icon={isPresent ? <CheckCircleOutlined /> : <WarningOutlined />}
            color={isPresent ? "success" : "warning"}
          >
            {val || "—"}
          </Tag>
        );
      },
    },
  ];

  return (
    <div style={{ padding: "4px" }}>

      <Card
        bordered={false}
        style={{ borderRadius: 12, boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)", overflow: "hidden" }}
        bodyStyle={{ padding: "30px" }}
      >
        {/* Top Header Section */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 30, borderBottom: "1px solid #f0f0f0", paddingBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <Avatar size={80} icon={<UserOutlined />} style={{ backgroundColor: "#13487D" }} />
            <div>
              <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#13487D" }}>{currentUser.name}</h2>
              <div style={{ color: "#666", fontSize: 15, marginTop: 2 }}>{currentUser.roleId?.name || "System User"}</div>
              <div style={{ color: "#888", fontSize: 13, marginTop: 4 }}>Joined On {new Date(currentUser.createdAt).toLocaleDateString()}</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 32 }}>
            <div>
              <div style={{ color: "#888", fontSize: 12, fontWeight: 500, marginBottom: 4 }}>EMAIL ADDRESS</div>
              <div style={{ fontWeight: 600, fontSize: 16, color: "#13487D" }}>{currentUser.email}</div>
            </div>
            <div>
              <div style={{ color: "#888", fontSize: 12, fontWeight: 500, marginBottom: 4 }}>QUALIFICATION</div>
              <div style={{ fontWeight: 600, fontSize: 16, color: "#333" }}>{currentUser.qualification || "N/A"}</div>
            </div>
          </div>
        </div>

        {/* Grid Body */}
        <Row gutter={32}>
          {/* Left Panel - Personal & Job Details */}
          <Col xs={24} md={8} style={{ borderRight: "1px solid #f0f0f0", paddingRight: 32 }}>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 600, color: "#444", fontSize: 13, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Email</div>
              <div style={{ color: "#1890ff", wordBreak: "break-all", fontSize: 15, fontWeight: 500 }}>{currentUser.email}</div>
            </div>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 600, color: "#444", fontSize: 13, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Phone No</div>
              <div style={{ color: "#333", fontSize: 15, fontWeight: 500 }}>{currentUser.mobileNo}</div>
            </div>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 600, color: "#444", fontSize: 13, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Current Address</div>
              <div style={{ color: "#555", fontSize: 15, lineHeight: 1.5 }}>{currentUser.currentAddress || "N/A"}</div>
            </div>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 600, color: "#444", fontSize: 13, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Permanent Address</div>
              <div style={{ color: "#555", fontSize: 15, lineHeight: 1.5 }}>{currentUser.permanentAddress || "N/A"}</div>
            </div>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 600, color: "#444", fontSize: 13, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Previous Company</div>
              <div style={{ color: "#555", fontSize: 15, fontWeight: 500 }}>{currentUser.previousCompany || "N/A"}</div>
            </div>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 600, color: "#444", fontSize: 13, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Previous Salary</div>
              <div style={{ color: "#555", fontSize: 15, fontWeight: 500 }}>{currentUser.previousSalary || "N/A"}</div>
            </div>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 600, color: "#444", fontSize: 13, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Reporting Manager</div>
              <div style={{ color: "#333", fontSize: 15, fontWeight: 500 }}>{currentUser.reportingManager?.name || "—"}</div>
            </div>
          </Col>

          {/* Right Panel - Tab & Document/Attendance Viewer */}
          <Col xs={24} md={16} style={{ paddingLeft: 32 }}>
            {/* Tabs headers */}
            <div style={{ borderBottom: "2px solid #f0f0f0", marginBottom: 24, display: "flex", gap: 32, fontSize: 15 }}>
              <div
                style={{
                  borderBottom: activeMainTab === "documents" ? "2px solid #1890ff" : "none",
                  paddingBottom: 12,
                  color: activeMainTab === "documents" ? "#1890ff" : "#555",
                  fontWeight: activeMainTab === "documents" ? 600 : 400,
                  cursor: "pointer"
                }}
                onClick={() => setActiveMainTab("documents")}
              >
                Documents
              </div>
              <div
                style={{
                  borderBottom: activeMainTab === "attendance" ? "2px solid #1890ff" : "none",
                  paddingBottom: 12,
                  color: activeMainTab === "attendance" ? "#1890ff" : "#555",
                  fontWeight: activeMainTab === "attendance" ? 600 : 400,
                  cursor: "pointer"
                }}
                onClick={() => setActiveMainTab("attendance")}
              >
                Attendance
              </div>
              <div
                style={{
                  borderBottom: activeMainTab === "salary" ? "2px solid #1890ff" : "none",
                  paddingBottom: 12,
                  color: activeMainTab === "salary" ? "#1890ff" : "#555",
                  fontWeight: activeMainTab === "salary" ? 600 : 400,
                  cursor: "pointer"
                }}
                onClick={() => setActiveMainTab("salary")}
              >
                Salary Records
              </div>
            </div>

            {/* TAB CONTENT: Documents */}
            {activeMainTab === "documents" && (
              docTabs.length > 0 ? (
                <>
                  {/* Document Pill selection */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                    <Space wrap size={10}>
                      {docTabs.map((tab) => (
                        <Button
                          key={tab.key}
                          onClick={() => setActiveDocKey(tab.key)}
                          style={{
                            borderRadius: 6,
                            backgroundColor: activeDocKey === tab.key ? "#e6f7ff" : "#f5f5f5",
                            color: activeDocKey === tab.key ? "#1890ff" : "#555",
                            borderColor: activeDocKey === tab.key ? "#bae7ff" : "#d9d9d9",
                            fontWeight: activeDocKey === tab.key ? 600 : 400,
                            height: 36,
                          }}
                        >
                          {tab.label}
                        </Button>
                      ))}
                    </Space>
                    <Button type="link" onClick={downloadAllDocs} style={{ color: "#1890ff", fontWeight: 600, fontSize: 14 }}>
                      Download All
                    </Button>
                  </div>

                  {/* Selection info bar */}
                  {selectedDoc && (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f9fbfd", border: "1px solid #e6f0fa", borderRadius: 6, padding: "12px 18px", marginBottom: 20 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <FilePdfOutlined style={{ fontSize: 20, color: "#ff4d4f" }} />
                        <span style={{ fontWeight: 500, color: "#333", fontSize: 14 }}>{selectedDoc.filename}</span>
                      </div>
                      <a href={getFileUrl(selectedDoc.filename)} target="_blank" rel="noreferrer" style={{ color: "#1890ff", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                        Open <EyeOutlined />
                      </a>
                    </div>
                  )}

                  {/* Embed Sandbox Preview */}
                  {selectedDoc && (
                    <div style={{ border: "1px solid #e8e8e8", borderRadius: 8, overflow: "hidden", background: "#fafafa", boxShadow: "0 2px 8px rgba(0,0,0,0.02)" }}>
                      {isImageFile(selectedDoc.filename) ? (
                        <div style={{ padding: 30, textAlign: "center" }}>
                          <img
                            src={getFileUrl(selectedDoc.filename)}
                            alt={selectedDoc.label}
                            style={{ maxWidth: "100%", maxHeight: "500px", objectFit: "contain", borderRadius: 4 }}
                          />
                        </div>
                      ) : (
                        <iframe
                          src={getFileUrl(selectedDoc.filename)}
                          title={selectedDoc.label}
                          width="100%"
                          height="550px"
                          style={{ border: "none" }}
                        />
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ padding: "40px", textAlign: "center", color: "#888" }}>
                  No onboarding documents uploaded for this user.
                </div>
              )
            )}

            {/* TAB CONTENT: Attendance */}
            {activeMainTab === "attendance" && (
              <>
                <div className="att-history-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                  <Title level={5} className="att-title" style={{ margin: 0, color: "#13487D" }}>
                    Attendance History
                  </Title>
                  <div className="att-history-controls" style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <Button onClick={() => setOpenCorrection(true)}>
                      Correction
                    </Button>
                    <input
                      type="month"
                      value={month}
                      min="2025-10"
                      max={moment().format("YYYY-MM")}
                      onChange={(e) => setMonth(e.target.value)}
                      className="att-month-input"
                      style={{
                        padding: "4px 10px",
                        borderRadius: "6px",
                        border: "1px solid #d9d9d9",
                        outline: "none",
                        height: "32px"
                      }}
                    />
                  </div>
                </div>

                <Table
                  dataSource={attendanceHistory}
                  columns={attendanceColumns}
                  rowKey={(r, i) => r.id || r.date || i}
                  pagination={false}
                  loading={attendanceLoading}
                  scroll={{ y: 460 }}
                  size="small"
                  locale={{
                    emptyText: (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No records yet. Punch in to get started."
                      />
                    ),
                  }}
                  rowClassName={(r) => (r.date === todayKey ? "att-today-row" : "")}
                />
              </>
            )}

            {/* TAB CONTENT: Salary Records */}
            {activeMainTab === "salary" && (
              <PayrollRecords userId={id} />
            )}
          </Col>
        </Row>
      </Card>

      {/* ── Correction Dialog ─────────────────────────── */}
      <AttendanceRegularizationDialog
        open={openCorrection}
        onClose={() => setOpenCorrection(false)}
      />
    </div>
  );
};

export default UserDetailView;
